/**
 * One dependency, as a line you read rather than a row of controls.
 *
 * A dependency is read far more often than it is edited, so the collapsed row
 * carries no form controls at all — the linked issue's number and title, then
 * three quiet chips saying what kind of link it is. Clicking opens the editor
 * in place. The previous row put six live controls on one 11px line and gave
 * the title whatever space was left, which was almost none.
 *
 * The editor opens with the edge read out as a sentence, and every choice in it
 * has an ⓘ saying what it does: "FS", "lag" and "hard/soft" are scheduling
 * jargon most people filing a site issue have never had to learn.
 */
import React, { useEffect, useRef, useState } from "react";
import { CornerDownRight, CornerUpRight, Link2, Link2Off, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { fill } from "@/lib/i18n/fill";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { GanttStagedBar } from "@/domains/pm/components/issues/gantt/GanttConfirmBar";
import type {
  IssueDependencyHardness,
  IssueDependencyType,
  PMIssue,
  PMIssueDependency,
} from "@/domains/pm/types";
import type { EdgePatch } from "@/domains/pm/hooks/useStagedSchedule";
import { DEPENDENCY_TYPES } from "./dependencyConstants";
import { InfoHint } from "./InfoHint";

export interface DependencyRowProps {
  dep: PMIssueDependency;
  /** The issue at the other end, or undefined when it is off the loaded page. */
  other: PMIssue | undefined;
  /** "waiting" — the other issue comes first. "drives" — this one does. */
  direction: "waiting" | "drives";
  /** Edited or added in the Gantt but not yet confirmed. */
  staged: boolean;
  /** Read the edge without being able to change or remove it. */
  readOnly?: boolean;
  expanded: boolean;
  onToggle: () => void;
  onChange: (patch: EdgePatch) => void;
  onRemove: () => void;
}

export function DependencyRow({
  dep,
  other,
  direction,
  staged,
  readOnly = false,
  expanded,
  onToggle,
  onChange,
  onRemove,
}: DependencyRowProps) {
  const { t } = useLanguage();
  const copy = t.pm.issues.dependencies;
  const DirectionIcon = direction === "waiting" ? CornerDownRight : CornerUpRight;
  const open = expanded && !readOnly;
  const rigid = dep.hardness === "hard";

  return (
    <li
      className={cn(
        "rounded-md border-l-2 border-transparent transition-colors",
        open && "border-primary"
      )}
    >
      <div
        className={cn(
          "group flex items-center gap-2 rounded-md px-2 py-1.5",
          !open && "hover:bg-accent",
          staged && GanttStagedBar.className
        )}
      >
        <button
          type="button"
          onClick={readOnly ? undefined : onToggle}
          aria-expanded={readOnly ? undefined : expanded}
          disabled={readOnly}
          className="flex min-w-0 flex-1 items-center gap-2 text-left disabled:cursor-default"
        >
          <DirectionIcon className="h-3.5 w-3.5 shrink-0 text-primary" />
          <span className="shrink-0 text-[11px] font-semibold tabular-nums text-primary">
            {other ? `#${other.issue_number}` : copy.offPage}
          </span>
          <span className="min-w-0 flex-1 truncate text-sm">{other?.title ?? ""}</span>

          {staged && <Chip tone="staged">{copy.staged}</Chip>}
          <RowMeta>
            <span title={copy[`type${dep.dep_type}`]}>{copy[`abbr${dep.dep_type}`]}</span>
            {/* Most edges have no lag; a "0" on every row would be pure noise. */}
            {dep.lag_days !== 0 && (
              <span>{dep.lag_days > 0 ? `+${dep.lag_days}d` : `${dep.lag_days}d`}</span>
            )}
            <span className="text-primary">{rigid ? copy.rigid : copy.flexible}</span>
          </RowMeta>
        </button>

        {!readOnly && (
          <button
            type="button"
            aria-label={copy.remove}
            onClick={onRemove}
            className={cn(
              "shrink-0 rounded p-0.5 text-muted-foreground transition-opacity hover:bg-accent hover:text-primary focus-visible:opacity-100 group-hover:opacity-100",
              // Hover-only is unreachable on a tablet; an open row shows it too.
              open ? "opacity-100" : "opacity-0 [@media(hover:none)]:opacity-100"
            )}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {open && (
        <DependencyEditor dep={dep} other={other} direction={direction} onChange={onChange} />
      )}
    </li>
  );
}

/**
 * What kind of link it is, read as one quiet line: `LA · +2d · Paindlik`.
 *
 * Boxed chips gave three one-word values the weight of three buttons on a row
 * whose point is the issue's title.
 */
function RowMeta({ children }: { children: React.ReactNode }) {
  const items = React.Children.toArray(children).filter(Boolean);
  return (
    <span className="flex shrink-0 items-center gap-1 text-[11px] font-medium text-muted-foreground">
      {items.map((item, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span aria-hidden="true">·</span>}
          {item}
        </React.Fragment>
      ))}
    </span>
  );
}

function Chip({
  children,
  tone = "staged",
  title,
}: {
  children: React.ReactNode;
  /** Only the staged marker is a chip; the rest of the row is plain text. */
  tone?: "staged";
  title?: string;
}) {
  return (
    <span
      title={title}
      className={cn(
        "inline-flex shrink-0 items-center gap-0.5 rounded px-1.5 py-0.5 text-[11px] font-medium",
        tone === "staged" && "bg-[#fef3c7] text-[#92400e]"
      )}
    >
      {children}
    </span>
  );
}

/** The edge read out: "This issue starts when #12 finishes · 2 days later · moves together". */
function edgeSummary(
  dep: DependencyDraft,
  other: PMIssue | undefined,
  direction: "waiting" | "drives",
  copy: ReturnType<typeof useLanguage>["t"]["pm"]["issues"]["dependencies"]
): string {
  const otherName = other ? `#${other.issue_number}` : copy.linkedIssue;
  const [earlier, later] =
    direction === "waiting" ? [otherName, copy.thisIssue] : [copy.thisIssue, otherName];
  const sentence = fill(copy[`summary${dep.dep_type as IssueDependencyType}`], { earlier, later });
  const parts = [`${sentence.charAt(0).toUpperCase()}${sentence.slice(1)}`];
  // A lag of zero is the default state; saying "no lag" on most edges is noise.
  const lagCount = Math.abs(dep.lag_days);
  if (lagCount !== 0) {
    const lagCopy = dep.lag_days > 0 ? copy.lagLater : copy.lagEarlier;
    parts.push(fill(lagCopy[lagCount === 1 ? "one" : "other"], { count: String(lagCount) }));
  }
  parts.push(dep.hardness === "hard" ? copy.summaryRigid : copy.summaryFlexible);
  return parts.join(" · ");
}

// Typed here rather than as `<OptionGroup<…>>`: the dev server's tagger cannot
// parse explicit JSX type arguments.
const HARDNESS_OPTIONS: IssueDependencyHardness[] = ["hard", "soft"];

/**
 * An edge being edited — saved, or not saved yet.
 *
 * A dependency added from the panel starts with NO relationship: it is the one
 * thing the person has to decide, and defaulting it to Finish → start meant
 * most edges kept a relationship nobody chose.
 */
export interface DependencyDraft {
  dep_type: IssueDependencyType | null;
  lag_days: number;
  hardness: IssueDependencyHardness;
}

/** The editor. Opens under the row it belongs to, reading as one card with it. */
export function DependencyEditor({
  dep,
  other,
  direction,
  onChange,
  isDraft = false,
}: {
  dep: DependencyDraft;
  other: PMIssue | undefined;
  direction: "waiting" | "drives";
  onChange: (patch: EdgePatch) => void;
  isDraft?: boolean;
}) {
  const { t } = useLanguage();
  const copy = t.pm.issues.dependencies;
  const rigid = dep.hardness === "hard";

  return (
    <div className="space-y-4 px-3 pb-3 pt-1">
      <p className="rounded-md border border-foreground bg-background px-2.5 py-1.5 text-xs font-medium text-foreground">
        {dep.dep_type ? edgeSummary(dep, other, direction, copy) : copy.pickRelationship}
      </p>

      <Field
        label={copy.relationship}
        info={
          <>
            <p>{copy.relationshipInfo}</p>
            <ul className="space-y-1">
              {DEPENDENCY_TYPES.map((type) => (
                <li key={type}>
                  <span className="font-semibold">{copy[`type${type}`]}</span> —{" "}
                  {copy[`type${type}Info`]}
                </li>
              ))}
            </ul>
          </>
        }
      >
        <OptionGroup
          label={copy.relationship}
          className="grid grid-cols-2 gap-1.5"
          options={DEPENDENCY_TYPES}
          value={dep.dep_type ?? undefined}
          selectionFollowsFocus={!isDraft}
          onChange={(type) => onChange({ dep_type: type })}
          render={(type, selected) => (
            <>
              <TypeDiagram type={type} selected={selected} />
              <span>{copy[`type${type}`]}</span>
            </>
          )}
        />
      </Field>

      <Field label={copy.lag} info={<p>{copy.lagInfo}</p>} hint={copy.lagHint}>
        <LagStepper value={dep.lag_days} onCommit={(v) => onChange({ lag_days: v })} />
      </Field>

      <Field
        label={copy.movesTogether}
        info={
          <>
            <p>{copy.movesTogetherInfo}</p>
            <p>
              <span className="font-semibold">{copy.rigid}</span> — {copy.rigidInfo}
            </p>
            <p>
              <span className="font-semibold">{copy.flexible}</span> — {copy.flexibleInfo}
            </p>
          </>
        }
        hint={rigid ? copy.rigidHint : copy.flexibleHint}
      >
        <OptionGroup
          label={copy.movesTogether}
          className="grid grid-cols-2 gap-1.5"
          options={HARDNESS_OPTIONS}
          value={dep.hardness}
          onChange={(hardness) => onChange({ hardness })}
          render={(hardness) => {
            const Icon = hardness === "hard" ? Link2 : Link2Off;
            return (
              <>
                <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <span>{hardness === "hard" ? copy.rigid : copy.flexible}</span>
              </>
            );
          }}
        />
      </Field>
    </div>
  );
}

function Field({
  label,
  info,
  hint,
  children,
}: {
  label: string;
  info?: React.ReactNode;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-foreground/70">
          {label}
        </span>
        {info && <InfoHint label={label}>{info}</InfoHint>}
      </div>
      {children}
      {hint && <p className="text-[11px] leading-snug text-muted-foreground">{hint}</p>}
    </div>
  );
}

/**
 * A radio group drawn as buttons — the brand-red selected state reads at a
 * glance where a 12px native radio did not. Roving tabindex and arrow keys,
 * as a native group of radios would have.
 */
function OptionGroup<T extends string>({
  label,
  options,
  value,
  onChange,
  render,
  className,
  selectionFollowsFocus = true,
}: {
  label: string;
  options: readonly T[];
  /** `undefined` while nothing is chosen — a new edge has no relationship. */
  value: T | undefined;
  onChange: (next: T) => void;
  render: (option: T, selected: boolean) => React.ReactNode;
  className?: string;
  selectionFollowsFocus?: boolean;
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const [focusIndex, setFocusIndex] = useState(() =>
    value === undefined ? 0 : Math.max(0, options.indexOf(value))
  );

  useEffect(() => {
    if (value !== undefined) setFocusIndex(Math.max(0, options.indexOf(value)));
  }, [options, value]);

  const move = (from: number, by: number) => {
    const next = (from + by + options.length) % options.length;
    setFocusIndex(next);
    if (selectionFollowsFocus) onChange(options[next]);
    refs.current[next]?.focus();
  };

  return (
    <div role="radiogroup" aria-label={label} className={className}>
      {options.map((option, i) => {
        const selected = option === value;
        return (
          <button
            key={option}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={i === focusIndex ? 0 : -1}
            onFocus={() => setFocusIndex(i)}
            onClick={() => onChange(option)}
            onKeyDown={(e) => {
              if (e.key === "ArrowRight" || e.key === "ArrowDown") {
                e.preventDefault();
                move(i, 1);
              } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
                e.preventDefault();
                move(i, -1);
              } else if (!selectionFollowsFocus && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                onChange(option);
              }
            }}
            className={cn(
              "flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-left text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
              selected
                ? "border-primary bg-background text-primary"
                : "border-border bg-background text-foreground hover:border-primary/50"
            )}
          >
            {render(option, selected)}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Two bars and the arrow between them: which end of the earlier issue (top)
 * pins which end of the later one (bottom). Anchor x per type, bar spans chosen
 * so the arrow is always vertical.
 */
const DIAGRAM: Record<
  IssueDependencyType,
  { earlier: [number, number]; later: [number, number]; x: number }
> = {
  FS: { earlier: [3, 21], later: [21, 37], x: 21 },
  SS: { earlier: [5, 25], later: [5, 33], x: 5 },
  FF: { earlier: [3, 35], later: [15, 35], x: 35 },
  SF: { earlier: [19, 37], later: [3, 19], x: 19 },
};

function TypeDiagram({ type, selected }: { type: IssueDependencyType; selected: boolean }) {
  const { earlier, later, x } = DIAGRAM[type];
  return (
    <svg
      viewBox="0 0 40 20"
      className={cn("h-6 w-12 shrink-0", selected ? "text-primary" : "text-muted-foreground")}
      aria-hidden="true"
    >
      <rect
        x={earlier[0]}
        y={2}
        width={earlier[1] - earlier[0]}
        height={5}
        rx={1.5}
        fill="currentColor"
        opacity={0.45}
      />
      <rect
        x={later[0]}
        y={13}
        width={later[1] - later[0]}
        height={5}
        rx={1.5}
        fill="currentColor"
      />
      <line x1={x} y1={7} x2={x} y2={10.5} stroke="currentColor" strokeWidth={1.25} />
      <path d={`M${x - 2} 10 L${x + 2} 10 L${x} 12.5 Z`} fill="currentColor" />
    </svg>
  );
}

/**
 * The lag, in days.
 *
 * It commits on blur, Enter or a step — NOT on every keystroke. The previous
 * bare number input wrote (or staged) per character, so typing "12" issued two
 * writes, "-5" issued one for the intermediate "-", and clearing the field was
 * impossible because `Number("")` is 0 rather than NaN.
 */
function LagStepper({ value, onCommit }: { value: number; onCommit: (next: number) => void }) {
  const { t } = useLanguage();
  const [draft, setDraft] = useState(String(value));

  // Follow the edge when it changes underneath — a cascade, a realtime reload,
  // or the other end of a staged edit.
  useEffect(() => setDraft(String(value)), [value]);

  const commit = (raw: string) => {
    const next = raw.trim() === "" ? 0 : Number(raw);
    if (!Number.isFinite(next)) {
      setDraft(String(value));
      return;
    }
    const clamped = Math.max(-365, Math.min(365, Math.trunc(next)));
    setDraft(String(clamped));
    if (clamped !== value) onCommit(clamped);
  };

  const step = (by: number) => {
    const next = Math.max(-365, Math.min(365, value + by));
    if (next !== value) onCommit(next);
  };

  return (
    <div className="flex items-center gap-1">
      <StepButton label="−" onClick={() => step(-1)} />
      <input
        type="text"
        inputMode="numeric"
        value={draft}
        aria-label={t.pm.issues.dependencies.lag}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit((e.target as HTMLInputElement).value);
          }
        }}
        className="h-7 w-14 rounded-md border bg-background px-1 text-center text-xs tabular-nums focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
      />
      <StepButton label="+" onClick={() => step(1)} />
      <span className="pl-1 text-xs text-muted-foreground">{t.pm.issues.dependencies.days}</span>
    </div>
  );
}

function StepButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="h-7 w-7 rounded-md border bg-background text-sm leading-none text-muted-foreground hover:border-primary/50 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      {label}
    </button>
  );
}
